import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
	Button,
	Card,
	CardActions,
	CardContent,
	FormControl,
	FormHelperText,
	IconButton,
	InputAdornment,
	InputLabel,
	OutlinedInput,
	Typography,
} from '@mui/material';
import { Screensaver2FA } from 'components/Screensaver2FA';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { disable2FARequest, enable2FARequest } from 'redux/reducers/settings/reducer';
import {
	getDisable2FA,
	getDisable2FAError,
	getEnable2FA,
	getEnable2FAError,
	getPasswordConfirmed2FA,
	getStatus2FA,
} from 'redux/reducers/settings/selectors';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { WarningAlert } from 'ui/WarningAlert';

export const Enable2FA: FC = () => {
	const dispatch = useDispatch();
	const userInfo = useSelector(getUserInfo);
	const passwordConfirmed = useSelector(getPasswordConfirmed2FA);
	const status2FA = useSelector(getStatus2FA);
	const enable2FAError = useSelector(getEnable2FAError);
	const enable2FA = useSelector(getEnable2FA);
	const disable2FA = useSelector(getDisable2FA);
	const disable2FAError = useSelector(getDisable2FAError);

	const [currentPassword, setCurrentPassword] = useState('');
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);

	const handleCurrentPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentPassword(event.target.value);
	};

	const [code, setCode] = useState('');
	const handleCode = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCode(event.target.value);
	};

	const handleClickShowCurrentPassword = () => setShowCurrentPassword((show) => !show);
	const handleMouseDownCurrentPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const handleEnable2FA = () => {
		if (!userInfo?.email) return;
		if (status2FA?.enabled) {
			dispatch(disable2FARequest({ password: currentPassword, totp: code }));
			return;
		}
		dispatch(enable2FARequest({ password: currentPassword }));
	};

	return (
		<>
			<Card
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
				}}
			>
				<CardContent>
					<Typography
						color="primary"
						sx={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: 3, alignContent: 'start' }}
					>
						{status2FA?.enabled ? 'Disable' : 'Enable'} Two-Factor Authentication
					</Typography>
					{status2FA?.enabled ? (
						<>
							{/* <Typography
								variant="h6"
								sx={{ fontWeight: 'bold', marginBottom: 3, alignContent: 'start', color: 'green' }}
								color="success"
							>
								Two-Factor Authentication is enabled successfully!
							</Typography> */}
							<WarningAlert>
								It is strongly recommended to keep two-factor authentication (2FA) enabled as
								disabling it will lower your security level!
							</WarningAlert>
						</>
					) : (
						<>
							<Typography>
								For your security, we strongly recommend enabling 2FA on your account. After that,
								you will need to enter a one-time 6-digit code each time you are logging into your
								account or changing the password.
							</Typography>
							<WarningAlert>
								You should download and install an authenticator application, such as{' '}
								<b>Google Authenticator</b>
								{` `}
								or <b>FreeOTP</b>, on your mobile device. This app will generate one-time 6-digit
								access codes, which are required for logging into your account.
							</WarningAlert>
						</>
					)}
					{!passwordConfirmed && (
						<>
							<FormControl
								variant="outlined"
								fullWidth
								required
								sx={{ marginTop: 2 }}
								error={!!enable2FAError || !!disable2FAError}
							>
								<InputLabel htmlFor="outlined-adornment-password">Current password</InputLabel>
								<OutlinedInput
									sx={{ borderRadius: '12px' }}
									type={showCurrentPassword ? 'text' : 'password'}
									value={currentPassword}
									onChange={handleCurrentPassword}
									endAdornment={
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={handleClickShowCurrentPassword}
												onMouseDown={handleMouseDownCurrentPassword}
												edge="end"
											>
												{showCurrentPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									}
									label="Current password"
								/>
								<FormHelperText>{enable2FAError || disable2FAError}</FormHelperText>
							</FormControl>
							{status2FA?.enabled && (
								<FormControl
									variant="outlined"
									fullWidth
									required
									sx={{ marginTop: 2 }}
									error={!!enable2FAError || !!disable2FAError}
								>
									<InputLabel htmlFor="outlined-adornment-password">
										One-time 6-digit code
									</InputLabel>
									<OutlinedInput
										sx={{ borderRadius: '12px' }}
										type="number"
										value={code}
										onChange={handleCode}
										label="One-time 6-digit code"
									/>
									<FormHelperText>{enable2FAError || disable2FAError}</FormHelperText>
								</FormControl>
							)}
						</>
					)}
				</CardContent>
				<CardActions sx={{ display: 'flex', justifyContent: 'end' }}>
					<Button
						variant="contained"
						color="primary"
						sx={{ borderRadius: '12px' }}
						onClick={handleEnable2FA}
						disabled={currentPassword.length < 4}
					>
						{status2FA?.enabled ? 'Disable' : 'Enable'} 2FA
					</Button>
				</CardActions>
			</Card>
			<Screensaver2FA open={enable2FA || disable2FA} type={enable2FA ? 'enable' : 'disable'} />
		</>
	);
};
